// Accounts (organizations) data layer for the Sales workspace — account-centric
// licensing + direct/reseller. Reuses existing org/license endpoints + the new
// /organizations/:id/resold margin endpoint (A2).
import { apiRequest } from '@/lib/supabase';

export interface OrgRow {
  id: string;
  name: string;
  domain?: string | null;
  customer_kind?: 'direct' | 'reseller';
  org_kind?: 'account' | 'tenant';
  parent_id?: string | null;
  external_org_id?: string | null;
  active?: boolean;
  contact_email?: string | null;
  contact_name?: string | null;
  license_count?: number;
  active_licenses?: number;
  mrr?: number;
  created_at?: string;
}

// A Fleet install tied to this company (its own license or, for a tenant, the
// platform install it runs under). From GET /organizations/:id/installs.
export interface OrgInstall {
  instance_id: string | null;
  hostname?: string | null;
  product_version?: string | null;
  health_status?: string | null;
  last_seen_at?: string | null;
  active?: boolean;
  license_id?: string | null;
}

export interface OrgLicense {
  id: string;
  license_id: string; // VW-… display key
  tier: string;
  status: string;
  scope?: string;
  is_trial?: boolean;
  vendor_limit?: number;
  included_org_seats?: number;
  expires_at?: string | null;
}

export interface ResoldRow {
  organization_id?: string | null; // tenant account (linkable)
  name: string;
  seats: number;
  used: number;
  status: string;
  billing_state: string;
  mrr?: number; // what the partner bills this tenant (rate-card resold)
  since: string;
}
export interface ResoldResponse {
  customer_kind: string;
  downstream: ResoldRow[];
  economics: { wholesale_mrr: number; resold_mrr: number; margin_pct: number } | null;
}

// The kinds license_org_allocations.org_kind can hold (migration 46's CHECK). 'supplier' orgs are
// exempt from the platform licence's org pool. The types below derive from this list, and
// test/allocation-kind.test.ts compares it with the migrations, so it cannot drift from the schema
// without CI going red.
export const ALLOCATION_ORG_KINDS = ['tenant', 'supplier'] as const;
export type AllocationOrgKind = (typeof ALLOCATION_ORG_KINDS)[number];

// A tenant's licensing surface — its allocation under a platform license (it owns
// no license of its own). From GET /organizations/:id/allocation.
export interface TenantAllocation {
  org_kind?: AllocationOrgKind;
  // Addresses the license_org_allocations row for the per-org trial endpoints.
  allocation_id: string;
  external_org_id: string;
  billing_state: string;
  seats: number;
  used: number;
  ciic_limit: number | null;
  ciic_used: number | null;
  modules: Record<string, boolean>;
  escalated_license_id: string | null;
  last_seen_at: string | null;
  // Per-company commercial trial (migration 28): whether this allocation is on a
  // trial, the trial deadline, and the per-trial vendor ceiling override.
  is_trial: boolean;
  trial_expires_at: string | null;
  trial_vendor_limit: number | null;
  mrr: number;
  platform_license: { id: string; license_key: string; tier: string } | null;
  owner: { id: string; name: string } | null;
}

// One append-only trial_events row (GET /fleet/orgs/:allocationId/trial/history),
// newest-first. old_*→new_* capture the before/after clock & cap of each change.
export type TrialAction =
  | 'started'
  | 'extended'
  | 'shortened'
  | 'expired'
  | 'converted'
  | 'vendor_cap_changed';
export interface TrialEvent {
  id: string;
  action: TrialAction;
  old_expires_at: string | null;
  new_expires_at: string | null;
  old_vendor_cap: number | null;
  new_vendor_cap: number | null;
  actor: string | null;
  reason: string | null;
  created_at: string;
}

// Body for a trial adjustment. Presence semantics mirror the backend service:
//   · trial_expires_at: ISO string sets the deadline; null converts to paid;
//     OMIT to leave the clock untouched (e.g. a cap-only change).
//   · trial_vendor_limit: sets the per-trial vendor ceiling; null clears it.
export interface AdjustTrialBody {
  trial_expires_at?: string | null;
  trial_vendor_limit?: number | null;
  reason?: string;
}

function mapLicense(l: any): OrgLicense {
  return {
    id: l.id,
    license_id: l.license_id,
    tier: l.tier,
    status: l.status,
    scope: l.scope,
    is_trial: l.is_trial,
    vendor_limit: l.vendor_limit,
    included_org_seats: l.included_org_seats,
    expires_at: l.expires_at,
  };
}

export const getOrganizations = () => apiRequest<{ data?: OrgRow[] }>('/organizations?limit=200').then((r) => r.data || []);
export const getOrganization = (id: string) => apiRequest<OrgRow>(`/organizations/${id}`);
export const getOrgLicenses = (id: string) =>
  apiRequest<{ data?: any[] }>(`/licenses?organizationId=${id}&pageSize=200`).then((r) => (r.data || []).map(mapLicense));
export const getResold = (id: string) => apiRequest<ResoldResponse>(`/organizations/${id}/resold`);
export const getOrgInstalls = (id: string) => apiRequest<OrgInstall[]>(`/organizations/${id}/installs`);
export const getOrgAllocation = (id: string) => apiRequest<TenantAllocation>(`/organizations/${id}/allocation`);

export interface FleetAllocationSummary {
  organization_id: string | null;
  org_kind?: AllocationOrgKind | null;
  is_trial?: boolean;
  trial_expires_at?: string | null;
}
export const getFleetAllocationSummaries = () => apiRequest<FleetAllocationSummary[]>('/fleet/orgs');

// Per-org commercial trial (migration 28) — the operator PATCH + append-only
// history. allocationId is TenantAllocation.allocation_id (a license_org_allocations
// row id), NOT the organization id. Both require admin (backend requireAdmin).
export const adjustTrial = (allocationId: string, body: AdjustTrialBody) =>
  apiRequest<{ allocation: unknown; event: TrialEvent }>(`/fleet/orgs/${allocationId}/trial`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
export const getTrialHistory = (allocationId: string) =>
  apiRequest<TrialEvent[]>(`/fleet/orgs/${allocationId}/trial/history`);

// Entitlement sets by customer kind (reference data from the design handoff —
// definitions of what each kind gets, not metrics).
export const ENTITLEMENTS: Record<'direct' | 'platform', Array<{ k: string; on: boolean }>> = {
  direct: [
    { k: 'End-user app access', on: true },
    { k: 'Own vendor pool', on: true },
    { k: 'Standard support', on: true },
    { k: 'White-label / rebrand', on: false },
    { k: 'Sub-tenant provisioning', on: false },
    { k: 'Wholesale / margin pricing', on: false },
    { k: 'Consolidated downstream billing', on: false },
  ],
  platform: [
    { k: 'End-user app access', on: true },
    { k: 'Platform pool to resell', on: true },
    { k: 'Priority partner support', on: true },
    { k: 'White-label / rebrand', on: true },
    { k: 'Sub-tenant provisioning', on: true },
    { k: 'Wholesale / margin pricing', on: true },
    { k: 'Consolidated downstream billing', on: true },
  ],
};
